import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Field } from "@/components/common";
import { LanguageSwitcher } from "@/components/layout/AppShell";
import { api, errMsg } from "@/lib/api";
import { useLockout } from "@/components/security";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";

const HERO = "https://images.unsplash.com/photo-1768661770207-9aa46d5ed526?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export function AuthLayout({ title, subtitle, children, platform }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-12">
      <div className={`hidden lg:flex lg:col-span-7 relative ${platform ? "bg-[#071a19]" : "bg-slate-900"} text-white overflow-hidden`}>
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent" />
        <div className="relative z-10 p-14 flex flex-col justify-between w-full">
          <div className="flex items-center gap-2 font-heading font-semibold text-lg"><div className={`h-8 w-8 rounded ${platform ? "bg-teal-500 text-slate-950" : "bg-blue-500"} flex items-center justify-center font-bold`}>O</div>Orbis ERP</div>
          <div className="max-w-lg">
            <div className="eyebrow text-slate-400 mb-3">{platform ? "Platform operations" : "Multi-tenant business platform"}</div>
            <h2 className="text-4xl font-semibold tracking-tight leading-tight">{platform ? "One platform owner. Many isolated customers." : "ERP, CRM and accounting for every company you run."}</h2>
            <p className="text-slate-300 mt-4 text-sm leading-relaxed">Tenant-isolated by design. Multi-company. Kosovo-first tax localization. Audited from login to ledger.</p>
          </div>
          <div className="text-xs font-mono text-slate-500">v1.0 · Kosovo-first ERP, CRM, POS & accounting</div>
        </div>
      </div>
      <div className="lg:col-span-5 flex flex-col p-6 sm:p-10 lg:p-14 justify-center bg-background">
        <div className="flex justify-between items-center mb-10"><div className="lg:hidden font-heading font-semibold">Orbis ERP</div><LanguageSwitcher /></div>
        <div className="max-w-md w-full mx-auto rise">
          <h1 className="text-3xl font-semibold tracking-tight">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground mt-2">{subtitle}</p>}
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function MfaStep({ mfaToken, onDone }) {
  const { t } = useT();
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true); setErr("");
    try { const { data } = await api.post("/auth/mfa/verify", { mfa_token: mfaToken, code }); await onDone(data); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="mfa-step">
      <p className="text-sm text-muted-foreground">{t("auth.mfa.hint")}</p>
      <InputOTP maxLength={8} value={code} onChange={setCode} data-testid="mfa-code-input"><InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="bg-card h-12 w-12 text-lg" />)}</InputOTPGroup></InputOTP>
      <Input data-testid="mfa-backup-input" placeholder="…or paste a backup code" value={code} onChange={(e) => setCode(e.target.value)} className="bg-card font-mono" />
      {err && <p className="text-sm text-red-600" data-testid="mfa-error">{err}</p>}
      <Button className="w-full" data-testid="mfa-submit-btn" disabled={busy || code.length < 6} onClick={submit}>{t("auth.verify")}</Button>
    </div>
  );
}

const DEMO_ROLES = [
  ["Customer Admin", "demo@apexgroup.com", "Desktop"], ["Sales Manager", "sales@apexgroup.com", "Desktop · team scope"], ["Sales User", "sales.user@apexgroup.com", "Desktop · own records"],
  ["Field Agent", "agent@apexgroup.com", "Phone app · own customers"], ["Mobile Sales Manager", "mobile.manager@apexgroup.com", "Desktop"], ["Accountant", "accountant@apexgroup.com", "Desktop"],
  ["HR Manager", "hr@apexgroup.com", "Desktop"], ["Inventory Manager", "inventory@apexgroup.com", "Desktop"], ["Purchase Manager", "purchase@apexgroup.com", "Desktop"],
  ["Project Manager", "projects@apexgroup.com", "Desktop"], ["Helpdesk", "helpdesk@apexgroup.com", "Desktop"], ["Marketing", "marketing@apexgroup.com", "Desktop"],
  ["Cashier", "cashier@apexgroup.com", "POS"], ["Shop floor", "shopfloor@apexgroup.com", "Shop floor"], ["Employee", "employee@apexgroup.com", "Self-service"],
];
function DemoPicker({ onPick, busy }) {
  const [open, setOpen] = useState(false);
  return <div className="rounded-xl border border-amber-400/40 bg-amber-400/5 p-3" data-testid="demo-picker">
    <button type="button" data-testid="demo-picker-toggle" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-sm font-medium"><span>Try as…</span><span className="text-xs text-muted-foreground">{open ? "hide" : `${DEMO_ROLES.length} demo roles`}</span></button>
    {open && <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">{DEMO_ROLES.map(([name, email, hint]) => <button key={email} type="button" disabled={busy} data-testid={`demo-role-${email.split("@")[0].replace(/\./g, "-")}`} onClick={() => onPick(email)} className="text-left rounded-lg border bg-card px-3 py-2 hover:border-amber-400 hover:bg-amber-400/10 transition-colors disabled:opacity-50"><div className="text-sm font-medium leading-tight">{name}</div><div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div></button>)}</div>}
  </div>;
}

export default function LoginView() {
  const demo = process.env.REACT_APP_DEMO_MODE === "1";
  const { t } = useT();
  const { loginWithTokens } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfaToken, setMfaToken] = useState(null);
  const [rawErr, setRawErr] = useState("");
  const lockLeft = useLockout(rawErr);
  const finish = async (data) => { const me = await loginWithTokens({ access_token: data.access_token, refresh_token: data.refresh_token, scope: "tenant" }); navigate(me?.start_page || "/app/dashboard"); };
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/login", form);
      if (data.mfa_required) setMfaToken(data.mfa_token); else await finish(data);
    } catch (e2) { setErr(errMsg(e2)); setRawErr(String(e2?.response?.data?.detail || "")); } finally { setBusy(false); }
  };
  return (
    <AuthLayout title={mfaToken ? t("auth.mfa.title") : t("auth.login")} subtitle={mfaToken ? null : "Sign in to your company workspace."}>
      {mfaToken ? <MfaStep mfaToken={mfaToken} onDone={finish} /> : (
        <form onSubmit={submit} className="space-y-5" data-testid="login-form">
          {demo && <DemoPicker onPick={async (email) => { setBusy(true); setErr(""); try { const { data } = await api.post("/auth/login", { email, password: "Demo1234!" }); await finish(data); } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); } }} busy={busy} />}
          <Field label={t("auth.email")}><Input data-testid="login-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-card" /></Field>
          <Field label={t("auth.password")}><Input data-testid="login-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-card" /></Field>
          {err && <p className="text-sm text-red-600" data-testid="login-error">{err}{lockLeft > 0 && <span className="font-mono ml-1" data-testid="login-lock-timer">({Math.floor(lockLeft / 60)}:{String(lockLeft % 60).padStart(2, "0")})</span>}</p>}
          <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" data-testid="login-submit-btn" disabled={busy || lockLeft > 0}>{t("auth.login")}</Button>
          <div className="flex justify-between text-sm text-muted-foreground pt-2"><span>{t("auth.no_account")} <Link to="/signup" className="text-blue-600 font-medium" data-testid="go-signup-link">{t("auth.signup")}</Link></span><Link to="/forgot-password" className="text-blue-600 text-xs" data-testid="go-forgot-link">{t("auth.forgot")}</Link></div>
          <div className="text-right"><Link to="/platform-admin/login" className="text-teal-700 text-xs font-mono" data-testid="go-platform-login-link">{t("auth.platform_login")} →</Link></div>
        </form>
      )}
    </AuthLayout>
  );
}
